import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  Sparkles,
  Users,
  UserPlus,
  Flame,
  CreditCard,
  Euro,
  RefreshCw,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  pulseMetricsService,
  FunnelMetrics,
  TimeSeriesPoint,
  PulseEvent,
  CohortMatrix,
} from "@core/services/PulseMetricsService";
import { TrialFunnelKPI } from "@core/components/admin/TrialFunnelKPI";

type Range = 1 | 5 | 10 | 20 | 30 | 90 | 180 | 360;
const RANGES: Range[] = [1, 5, 10, 20, 30, 90, 180, 360];

const formatNumber = (n: number) => n.toLocaleString("fr-FR");

const formatRelative = (iso: string): string => {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
};

const maskEmail = (email: string | undefined): string => {
  if (!email || !email.includes("@")) return email ?? "—";
  const [local, domain] = email.split("@");
  const head = local.slice(0, 2);
  return `${head}${"•".repeat(Math.max(1, local.length - 2))}@${domain}`;
};

interface FunnelCardProps {
  icon: React.ReactNode;
  title: string;
  value: number;
  subtext: string;
  subtextTone?: "neutral" | "positive" | "negative";
  accent: string;
}

const FunnelCard: React.FC<FunnelCardProps> = ({
  icon,
  title,
  value,
  subtext,
  subtextTone = "neutral",
  accent,
}) => {
  const toneClass =
    subtextTone === "positive"
      ? "text-green-400"
      : subtextTone === "negative"
        ? "text-red-400"
        : "text-gray-400";
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6 flex-1 min-w-[180px]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide">
          {title}
        </h3>
        <div className={accent}>{icon}</div>
      </div>
      <div className="text-3xl font-bold text-white mb-1">
        {formatNumber(value)}
      </div>
      <div className={`text-xs ${toneClass}`}>{subtext}</div>
    </div>
  );
};

const ConversionArrow: React.FC<{ pct: number; label: string }> = ({
  pct,
  label,
}) => (
  <div className="flex flex-col items-center justify-center px-2 text-gray-500 min-w-[70px]">
    <ArrowRight className="w-4 h-4 mb-1" />
    <div className="text-sm font-semibold text-gray-300">{pct}%</div>
    <div className="text-[10px] text-gray-500">{label}</div>
  </div>
);

interface SamplePoll {
  title: string;
  slug: string;
  poll_type: string;
  created_at: string;
}

const shortUA = (ua: string | undefined | null): string => {
  if (!ua) return "UA inconnu";
  if (/HeadlessChrome/i.test(ua)) return "🤖 Headless Chrome";
  if (/Playwright/i.test(ua)) return "🤖 Playwright";
  if (/jsdom/i.test(ua)) return "🤖 jsdom";
  if (/Android/i.test(ua)) return "📱 Android";
  if (/iPhone|iPad/i.test(ua)) return "📱 iOS";
  if (/Windows/i.test(ua)) return "🪟 Windows";
  if (/Macintosh|Mac OS/i.test(ua)) return "🍎 macOS";
  if (/Linux/i.test(ua)) return "🐧 Linux";
  return ua.slice(0, 40);
};

const EventRow: React.FC<{
  event: PulseEvent;
  onTagFingerprint?: (fingerprint: string) => void;
}> = ({ event, onTagFingerprint }) => {
  const [expanded, setExpanded] = useState(false);
  const { badge, color } = (() => {
    switch (event.type) {
      case "new_paying":
        return {
          badge: "Nouveau payant",
          color: "bg-green-500/20 text-green-300",
        };
      case "churn":
        return { badge: "Churn", color: "bg-red-500/20 text-red-300" };
      case "engaged_anon":
        return {
          badge: "Anonyme engagé",
          color: "bg-blue-500/20 text-blue-300",
        };
      case "first_poll":
        return {
          badge: "1er sondage",
          color: "bg-purple-500/20 text-purple-300",
        };
    }
  })();

  const meta = event.meta as {
    plan?: string;
    email?: string;
    status?: string;
    polls_count?: number;
    fingerprint?: string;
    fingerprint_prefix?: string;
    poll_type?: string;
    poll_title?: string;
    poll_slug?: string;
    user_agent?: string;
    sample_polls?: SamplePoll[];
    is_test?: boolean;
  };

  const hasDetails =
    event.type === "engaged_anon" ||
    event.type === "first_poll" ||
    !!meta.email;

  const detailContent = (() => {
    switch (event.type) {
      case "new_paying":
      case "churn":
        return meta.email ? (
          <Link
            to={`/admin/users?search=${encodeURIComponent(meta.email)}`}
            className="hover:underline text-blue-300"
            title="Voir ce user dans l'admin"
          >
            {meta.plan ?? "—"}
            {event.type === "churn" && meta.status
              ? ` · ${meta.status}`
              : ""} · {maskEmail(meta.email)}
          </Link>
        ) : (
          <>
            {meta.plan ?? "—"} · {maskEmail(meta.email)}
          </>
        );
      case "engaged_anon":
        return (
          <>
            fp:{meta.fingerprint_prefix} · {meta.polls_count} sondages
            {meta.user_agent !== undefined && (
              <span className="ml-2 text-xs text-gray-400">
                · {shortUA(meta.user_agent)}
              </span>
            )}
          </>
        );
      case "first_poll":
        return meta.email ? (
          <Link
            to={`/admin/users?search=${encodeURIComponent(meta.email)}`}
            className="hover:underline text-blue-300"
          >
            {meta.poll_type ?? "—"}
            {meta.poll_title ? ` "${meta.poll_title.slice(0, 40)}"` : ""} ·{" "}
            {maskEmail(meta.email)}
          </Link>
        ) : (
          <>
            {meta.poll_type ?? "—"}
            {meta.poll_title ? ` "${meta.poll_title.slice(0, 40)}"` : ""}
            {meta.fingerprint_prefix && (
              <span className="ml-2 text-xs text-gray-400">
                · fp:{meta.fingerprint_prefix}
              </span>
            )}
          </>
        );
    }
  })();

  const isTest = !!meta.is_test;

  return (
    <>
      <tr
        className={`border-b border-white/5 hover:bg-white/5 ${isTest ? "opacity-60" : ""}`}
      >
        <td className="px-4 py-2">
          <button
            onClick={() => hasDetails && setExpanded((v) => !v)}
            disabled={!hasDetails}
            className="flex items-center gap-1 disabled:cursor-default"
          >
            {hasDetails ? (
              expanded ? (
                <ChevronDown className="w-3 h-3 text-gray-500" />
              ) : (
                <ChevronRight className="w-3 h-3 text-gray-500" />
              )
            ) : (
              <span className="w-3 h-3" />
            )}
            <span className={`text-xs px-2 py-1 rounded ${color}`}>
              {badge}
            </span>
            {isTest && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase tracking-wider ml-1">
                test
              </span>
            )}
          </button>
        </td>
        <td className="px-4 py-2 text-sm text-white">{detailContent}</td>
        <td className="px-4 py-2 text-xs text-gray-500 text-right">
          {formatRelative(event.at)}
        </td>
        <td className="px-4 py-2 text-right w-20">
          {(event.type === "engaged_anon" || event.type === "first_poll") &&
            meta.fingerprint &&
            onTagFingerprint && (
              <button
                onClick={() => onTagFingerprint(meta.fingerprint!)}
                className="text-[10px] px-2 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded hover:bg-amber-500/20"
                title="Exclure ce fingerprint des stats"
              >
                tag test
              </button>
            )}
        </td>
      </tr>
      {expanded && hasDetails && (
        <tr className="border-b border-white/10 bg-black/30">
          <td colSpan={4} className="px-10 py-3 text-xs">
            {event.type === "engaged_anon" && meta.sample_polls && (
              <div>
                <div className="text-gray-400 mb-2">
                  Fingerprint complet :{" "}
                  <span className="font-mono text-gray-300">
                    {meta.fingerprint}
                  </span>
                </div>
                <div className="text-gray-400 mb-2">
                  User-Agent :{" "}
                  <span className="font-mono text-gray-300">
                    {meta.user_agent ?? "inconnu"}
                  </span>
                </div>
                <div className="text-gray-400 mb-1">
                  Derniers sondages créés :
                </div>
                <ul className="space-y-1">
                  {meta.sample_polls.map((p) => (
                    <li
                      key={p.slug}
                      className="flex items-center justify-between gap-3"
                    >
                      <span className="text-gray-200 truncate">
                        <span className="text-gray-500 mr-2">
                          [{p.poll_type}]
                        </span>
                        {p.title}
                      </span>
                      <a
                        href={`/poll/${p.slug}/vote`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1 shrink-0"
                      >
                        voir <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {event.type === "first_poll" && meta.poll_title && (
              <div className="text-gray-300">
                Sondage :{" "}
                <span className="font-semibold">{meta.poll_title}</span>
                {meta.poll_slug && (
                  <a
                    href={`/poll/${meta.poll_slug}/vote`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-3 text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
                  >
                    voir <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            )}
            {(event.type === "new_paying" || event.type === "churn") &&
              meta.email && (
                <div className="text-gray-300">
                  Email complet :{" "}
                  <span className="font-mono">{meta.email}</span>
                </div>
              )}
          </td>
        </tr>
      )}
    </>
  );
};

export const PulseDashboard: React.FC = () => {
  const [funnel, setFunnel] = useState<FunnelMetrics | null>(null);
  const [series, setSeries] = useState<TimeSeriesPoint[]>([]);
  const [events, setEvents] = useState<PulseEvent[]>([]);
  const [cohorts, setCohorts] = useState<CohortMatrix | null>(null);
  const [range, setRange] = useState<Range>(90);
  const [includeTests, setIncludeTests] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (r: Range, includeTestsParam = false) => {
    setLoading(true);
    setError(null);
    try {
      const [f, s, e, c] = await Promise.all([
        pulseMetricsService.getFunnel(r),
        pulseMetricsService.getTimeSeries(r),
        pulseMetricsService.getRecentEvents(20, includeTestsParam),
        pulseMetricsService.getQuarterlyCohorts(4),
      ]);
      setFunnel(f);
      setSeries(s);
      setEvents(e);
      setCohorts(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  // Chargement au mount uniquement. Les changements de `range` ne déclenchent
  // PAS de rechargement automatique (les RPC Pulse, notamment
  // get_pulse_timeseries, sont lourdes et saturaient le pool Supabase).
  // L'utilisateur change la range puis clique sur "Rafraîchir" pour recharger.
  // Ref guard pour éviter le double-fire en React StrictMode dev.
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    load(90, includeTests);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTagFingerprint = async (fingerprint: string) => {
    if (
      !confirm(
        `Marquer ce fingerprint (${fingerprint.slice(0, 12)}…) comme test ?\nIl disparaîtra du feed Pulse.`,
      )
    )
      return;
    try {
      await pulseMetricsService.setFingerprintTestFlag(fingerprint, true);
      setEvents((prev) =>
        prev.filter(
          (e) =>
            !(
              e.type === "engaged_anon" &&
              (e.meta as { fingerprint?: string }).fingerprint === fingerprint
            ),
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erreur");
    }
  };

  return (
    <div className="min-h-screen-safe bg-background dark:bg-[#030712] text-foreground">
      <header className="relative z-10 border-b border-white/5 bg-black/20 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <div className="w-8 h-8 rounded bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-md font-semibold text-white">
                DooDates Admin
              </span>
            </Link>
            <nav className="flex items-center gap-4">
              <Link
                to="/admin"
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                Support
              </Link>
              <Link
                to="/admin/roadmap"
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                Roadmap
              </Link>
              <Link
                to="/admin/analytics"
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                Analytics
              </Link>
              <Link
                to="/admin/pulse"
                className="text-xs text-white hover:text-blue-300 transition-colors font-medium"
              >
                Pulse
              </Link>
              <Link
                to="/admin/users"
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                Users
              </Link>
              <Link
                to="/"
                className="text-xs text-gray-500 hover:text-white transition-colors"
              >
                Quitter l'admin
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-blue-400" />
            <div>
              <h1 className="text-2xl font-semibold">Pulse</h1>
              <p className="text-sm text-gray-400">
                Santé acquisition · mise à jour{" "}
                {funnel ? formatRelative(funnel.generatedAt) : "…"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-gray-400 cursor-pointer hover:bg-white/10">
              <input
                type="checkbox"
                checked={includeTests}
                onChange={(e) => {
                  setIncludeTests(e.target.checked);
                  load(range, e.target.checked);
                }}
                className="w-4 h-4"
              />
              Afficher les tests
            </label>
            <button
              onClick={() => load(range, includeTests)}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-50 transition-colors text-sm"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              Rafraîchir
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Sélecteur de période global — s'applique au funnel ET à la tendance */}
        <div className="mb-8 flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-3">
          <span className="text-xs uppercase tracking-wide text-gray-400 ml-2">
            Période (toute la page)
          </span>
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  range === r
                    ? "bg-blue-500 text-white"
                    : "text-gray-400 hover:text-white hover:bg-white/10"
                }`}
              >
                {r}j
              </button>
            ))}
          </div>
        </div>

        {/* Bloc 1 — Funnel */}
        <section className="mb-10">
          <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-4">
            Funnel — {range}j glissants
          </h2>
          <div className="flex flex-wrap items-stretch gap-2">
            <FunnelCard
              icon={<Users className="w-5 h-5" />}
              title={`Anonymes ${range}j`}
              value={funnel?.anonymous.current ?? 0}
              subtext={
                funnel
                  ? `${funnel.anonymous.deltaPct >= 0 ? "+" : ""}${funnel.anonymous.deltaPct}% vs ${range}j préc.`
                  : "—"
              }
              subtextTone={
                funnel
                  ? funnel.anonymous.deltaPct >= 0
                    ? "positive"
                    : "negative"
                  : "neutral"
              }
              accent="text-blue-400"
            />
            <ConversionArrow
              pct={funnel?.conversion.anonToSignup ?? 0}
              label="anon → inscrit"
            />
            <FunnelCard
              icon={<UserPlus className="w-5 h-5" />}
              title="Inscrits"
              value={funnel?.signedUp.total ?? 0}
              subtext={
                funnel ? `+${funnel.signedUp.new30d} sur ${range}j` : "—"
              }
              subtextTone="positive"
              accent="text-purple-400"
            />
            <ConversionArrow
              pct={funnel?.conversion.signupToActive ?? 0}
              label="inscrits → actifs"
            />
            <FunnelCard
              icon={<Flame className="w-5 h-5" />}
              title={`Actifs ${range}j`}
              value={funnel?.active7d.count ?? 0}
              subtext={
                funnel ? `${funnel.active7d.pctOfSignedUp}% des inscrits` : "—"
              }
              accent="text-orange-400"
            />
            <ConversionArrow
              pct={funnel?.conversion.activeToPaying ?? 0}
              label="actifs → payants"
            />
            <FunnelCard
              icon={<CreditCard className="w-5 h-5" />}
              title="Payants"
              value={funnel?.paying.count ?? 0}
              subtext={
                funnel
                  ? `+${funnel.paying.new30d} / −${funnel.paying.churn30d} (${range}j)`
                  : "—"
              }
              subtextTone={
                funnel
                  ? funnel.paying.new30d >= funnel.paying.churn30d
                    ? "positive"
                    : "negative"
                  : "neutral"
              }
              accent="text-green-400"
            />
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                  MRR
                </h3>
                <Euro className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-3xl font-bold text-white">
                {funnel
                  ? `${formatNumber(Math.round(funnel.paying.mrrEur))} €`
                  : "—"}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {funnel
                  ? `ARR ≈ ${formatNumber(Math.round(funnel.paying.mrrEur * 12))} €`
                  : ""}
              </div>
            </div>
          </div>
        </section>

        {/* Bloc 1bis — L'entonnoir de l'ESSAI, déplacé ici depuis
            /admin/analytics le 18/09. Sa place est sous le funnel général :
            c'est la même histoire, un cran plus bas. Anonyme → inscrit →
            actif → payant dit le parcours ; l'essai dit ce qui se passe entre
            « inscrit » et « payant », qui est précisément ce que ce chantier
            cherche à provoquer.

            Un écart à connaître, mesuré le 18/09 et non supposé : la colonne
            « plan » de la liste ci-dessous vient de `get_pulse_users`, qui
            renvoie `COALESCE(profiles.plan_type, 'free')`. L'essai n'écrit PAS
            dans `profiles` — il écrit dans `product_subscriptions` — donc un
            essayeur s'affiche « free » dans cette liste alors que le tunnel le
            compte en essai. Les deux sont justes chacun de leur côté ; la
            contradiction est de lecture. Le remède est une redéfinition de
            `get_pulse_users` avec l'overlay de `product_subscriptions`, à
            faire dans une migration séparée.

            En revanche le compteur « Payants » et le MRR sont SAINS, vérifié
            sur la définition vive de `get_pulse_funnel` : ils filtrent sur
            `lemonsqueezy_status = 'active'`, jamais sur `plan_type`. Un
            essayeur ne peut donc pas être compté comme client — c'est le
            risque n° 13 du plan, et il est levé. */}
        <section className="mb-10">
          <TrialFunnelKPI />
        </section>

        {/* Bloc 2 — Time series */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm uppercase tracking-wide text-gray-500">
              Tendance ({range}j)
            </h2>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#9CA3AF"
                  style={{ fontSize: 11 }}
                  tickFormatter={(v) => v?.slice(5)}
                />
                <YAxis stroke="#9CA3AF" style={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1F2937",
                    border: "1px solid #374151",
                    borderRadius: 8,
                    color: "white",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="anonymous"
                  name="Anonymes"
                  stroke="#60A5FA"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="signed_up"
                  name="Inscrits"
                  stroke="#A78BFA"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="active"
                  name="Actifs 7j"
                  stroke="#FB923C"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="paying"
                  name="Payants"
                  stroke="#34D399"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Bloc Cohortes trimestrielles */}
        <section className="mb-10">
          <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-2">
            Rétention par cohorte trimestrielle
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            % d'inscrits de la cohorte encore actifs au trimestre N. Un
            sondage-date se crée ~1×/trimestre, donc un utilisateur actif à Q+1
            est une bonne nouvelle.
          </p>
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
            {!cohorts || cohorts.cohorts.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                Pas assez de données pour afficher les cohortes.
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5 text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-4 py-2 text-left font-medium">Cohorte</th>
                    <th className="px-4 py-2 text-right font-medium w-20">
                      Taille
                    </th>
                    {Array.from({ length: cohorts.max_offset + 1 }, (_, i) => (
                      <th
                        key={i}
                        className="px-4 py-2 text-center font-medium w-20"
                      >
                        {i === 0 ? "Q0 (signup)" : `Q+${i}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohorts.cohorts.map((c) => (
                    <tr
                      key={c.cohort_quarter}
                      className="border-b border-white/5"
                    >
                      <td className="px-4 py-2 text-sm text-white font-mono">
                        {c.cohort_quarter}
                      </td>
                      <td className="px-4 py-2 text-right text-sm text-gray-300">
                        {c.size}
                      </td>
                      {c.retention.map((pt) => {
                        const pct =
                          c.size > 0 ? (pt.retained / c.size) * 100 : 0;
                        const bgOpacity = Math.min(pct / 100, 1) * 0.6;
                        const bg = `rgba(52, 211, 153, ${bgOpacity})`;
                        return (
                          <td
                            key={pt.q_offset}
                            className="px-4 py-2 text-center text-sm"
                            style={{ backgroundColor: bg }}
                            title={`${pt.retained}/${c.size}`}
                          >
                            <span className="font-semibold text-white">
                              {pt.retained}
                            </span>
                            <span className="text-gray-400 text-xs ml-1">
                              ({Math.round(pct)}%)
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Bloc 3 — Events */}
        <section className="mb-10">
          <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-4">
            Derniers mouvements
          </h2>
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            {events.length === 0 && !loading ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                Aucun mouvement récent.
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="px-4 py-2 text-left text-xs uppercase tracking-wide text-gray-400 font-medium w-40">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs uppercase tracking-wide text-gray-400 font-medium">
                      Détail
                    </th>
                    <th className="px-4 py-2 text-right text-xs uppercase tracking-wide text-gray-400 font-medium w-32">
                      Quand
                    </th>
                    <th className="px-4 py-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e, i) => (
                    <EventRow
                      key={`${e.type}-${e.at}-${i}`}
                      event={e}
                      onTagFingerprint={handleTagFingerprint}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};
